'use client';
import Icon from '@/components/ui/AppIcon';

interface CategoryPerformance {
  id: string;
  name: string;
  accuracy: number;
  color: string;
}

interface PerformanceAnalyticsProps {
  categories?: CategoryPerformance[];
}

const PerformanceAnalytics = ({ categories }: PerformanceAnalyticsProps) => {
  const defaultCategories: CategoryPerformance[] = [
    { id: 'cat_nav', name: 'Navigation', accuracy: 0, color: 'bg-primary' },
    { id: 'cat_weather', name: 'Meteorology', accuracy: 0, color: 'bg-accent' },
    { id: 'cat_regs', name: 'Regulations', accuracy: 0, color: 'bg-success' },
    { id: 'cat_systems', name: 'Aircraft Systems', accuracy: 0, color: 'bg-warning' },
    { id: 'cat_performance', name: 'Performance', accuracy: 0, color: 'bg-primary' },
    { id: 'cat_aero', name: 'Aerodynamics', accuracy: 0, color: 'bg-accent' },
  ];

  const displayCategories = categories || defaultCategories;
  const lowestCategory = displayCategories.length > 0 
    ? displayCategories.reduce((prev, current) => 
        (current.accuracy < prev.accuracy) ? current : prev
      )
    : null;

  return (
    <section className="bg-card rounded-2xl p-4 sm:p-6 md:p-8 border border-border">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-1">Performance by Category</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Your accuracy across all topics</p>
        </div>
        <Icon name="ChartBarIcon" size={20} className="text-muted-foreground sm:w-6 sm:h-6" />
      </div>
      <div className="space-y-3 sm:space-y-4">
        {displayCategories?.map((category) => (
          <div key={category?.id}>
            <div className="flex items-center justify-between mb-1.5 sm:mb-2">
              <span className="text-xs sm:text-sm font-medium text-foreground">{category?.name}</span>
              <span className="text-xs sm:text-sm font-semibold text-foreground">{category?.accuracy}%</span>
            </div>
            <div className="w-full h-2.5 sm:h-3 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${category?.color} transition-all`}
                style={{ width: `${category?.accuracy}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      {lowestCategory && lowestCategory.accuracy > 0 && (
        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 sm:gap-3">
            <Icon name="LightBulbIcon" size={18} className="text-warning sm:w-5 sm:h-5 flex-shrink-0" />
            <div className="text-xs sm:text-sm text-muted-foreground">
              Focus on <span className="font-semibold text-foreground">{lowestCategory.name}</span> to improve your overall score
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default PerformanceAnalytics